import { useState, useMemo } from 'react'
import { useGameStore } from '@/store/gameStore'
import { getRosterCap, formatPotential, type Player } from '@/config/playersConfig'
import { getPlayerStage, STAGE_DEFINITIONS, type PlayerStage } from '@/config/playerStageConfig'
import { getCoachEffects, getNegotiationEffects } from '@/game/systems/staffEffects'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { UserPlus, ArrowLeftRight, AlertTriangle, Handshake, Scale } from 'lucide-react'
import { PlayerAvatar } from '../components/PlayerAvatar'
import { PlayerDetailModal } from '../components/PlayerDetailModal'
import { PlayerCompareModal } from '../components/PlayerCompareModal'
import { playerPortraitSrc } from '../helpers/portraits'
import { StageBadge } from './RosterTab'
import { SectionDeck, EmptyStateRich } from '../components/CourtVision'

type SortKey = 'level' | 'potential' | 'fee' | 'age'

const SORT_LABELS: Record<SortKey, string> = {
  level: 'Nivel',
  potential: 'Potencial',
  fee: 'Precio',
  age: 'Edad',
}

const SORTERS: Record<SortKey, (a: Player, b: Player) => number> = {
  level: (a, b) => b.level - a.level,
  potential: (a, b) => b.potential - a.potential,
  fee: (a, b) => a.signingFee - b.signingFee,
  age: (a, b) => a.age - b.age,
}

export function TransferMarketTabContent() {
  const { money, players, transferMarket, signPlayer, negotiateSigningFee, staff, clubLevel } = useGameStore()
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null)
  const [stageFilter, setStageFilter] = useState<PlayerStage | 'all'>('all')
  const [sortKey, setSortKey] = useState<SortKey>('level')
  const [compareIds, setCompareIds] = useState<string[]>([])

  const rosterCap = getRosterCap(clubLevel)
  const rosterFull = players.length >= rosterCap
  const bestRosterLevel = players.reduce((max, p) => Math.max(max, p.level), 0)
  const detailPlayer = transferMarket.find(p => p.id === detailPlayerId) ?? null
  const { scouting } = getCoachEffects(staff)
  const { successChance } = getNegotiationEffects(staff)

  const visible = useMemo(() => {
    const filtered = stageFilter === 'all'
      ? transferMarket
      : transferMarket.filter(p => getPlayerStage(p.age) === stageFilter)
    return [...filtered].sort(SORTERS[sortKey])
  }, [transferMarket, stageFilter, sortKey])

  const comparePlayers = useMemo((): [Player, Player] | null => {
    if (compareIds.length !== 2) return null
    // A compare slot can be a market candidate or a roster player
    const pool = [...transferMarket, ...players]
    const a = pool.find(p => p.id === compareIds[0])
    const b = pool.find(p => p.id === compareIds[1])
    return a && b ? [a, b] : null
  }, [compareIds, transferMarket, players])

  const toggleCompare = (id: string) => {
    setCompareIds(prev => prev.includes(id)
      ? prev.filter(x => x !== id)
      : [...prev.slice(-1), id])
  }

  const handleNegotiate = (p: Player) => {
    const result = negotiateSigningFee(p.id)
    EventBus.emit(GameEvents.NOTIFY, result === 'accepted'
      ? { message: `🤝 ${p.name} acepta rebajar su fichaje un 15%`, type: 'success' }
      : { message: `${p.name} se ofende con la oferta: su precio sube un 5%`, type: 'error' })
  }

  if (transferMarket.length === 0) {
    return (
      <EmptyStateRich
        icon={ArrowLeftRight}
        title="El mercado está en calma"
        sub="Cada semana llegan nuevos jugadores en busca de club. Vuelve pronto"
      />
    )
  }

  return (
    <div>
      <PlayerDetailModal
        player={detailPlayer}
        mode="market"
        onClose={() => setDetailPlayerId(null)}
        onAction={() => {
          if (detailPlayer && signPlayer(detailPlayer)) setDetailPlayerId(null)
        }}
        actionDisabled={!detailPlayer || rosterFull || money < detailPlayer.signingFee}
      />
      <PlayerCompareModal
        players={comparePlayers}
        scouted={scouting}
        onClose={() => setCompareIds([])}
      />

      <SectionDeck
        title="Mercado de fichajes"
        icon={ArrowLeftRight}
        meta={`Plantilla ${players.length}/${rosterCap}`}
      >
        {rosterFull && (
          <div style={{ marginBottom: '0.6rem' }}>
            <span className="pill pill-warning">
              <AlertTriangle size={10} /> Plantilla completa: rescinde un contrato antes de fichar
            </span>
          </div>
        )}

        {/* Filters + sort */}
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.6rem' }}>
          {(['all', 'rising', 'peak', 'veteran'] as const).map(stage => (
            <button
              key={stage}
              className="pill"
              onClick={() => setStageFilter(stage)}
              style={{
                cursor: 'pointer',
                background: stageFilter === stage ? 'var(--gold-fill)' : 'transparent',
                color: stageFilter === stage ? 'var(--gold)' : 'var(--text-secondary)',
                border: '1px solid var(--glass-border)',
              }}
            >
              {stage === 'all' ? 'Todos' : STAGE_DEFINITIONS[stage].label}
            </button>
          ))}
          <select
            value={sortKey}
            onChange={e => setSortKey(e.target.value as SortKey)}
            style={{
              marginLeft: 'auto', fontSize: '0.72rem', padding: '0.2rem 0.4rem',
              background: 'var(--bg-surface)', color: 'var(--text-secondary)',
              border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)',
            }}
          >
            {(Object.keys(SORT_LABELS) as SortKey[]).map(key => (
              <option key={key} value={key}>Ordenar: {SORT_LABELS[key]}</option>
            ))}
          </select>
        </div>

        {/* Compare hint */}
        {compareIds.length === 1 && (
          <div className="hint-box" style={{ marginBottom: '0.6rem', fontSize: '0.72rem' }}>
            <Scale size={12} style={{ display: 'inline', verticalAlign: '-2px' }} /> Marca
            "Comparar" en otro candidato para verlos frente a frente.
          </div>
        )}

        <div className="space-y-2">
          {visible.map(p => {
            const canSign = !rosterFull && money >= p.signingFee
            const betterThanBest = p.level > bestRosterLevel
            const comparing = compareIds.includes(p.id)

            return (
              <div
                key={p.id}
                className="row-tile"
                style={{
                  flexDirection: 'column', alignItems: 'stretch', gap: '0.45rem',
                  opacity: canSign ? 1 : 0.6, cursor: 'pointer',
                  outline: comparing ? '2px solid var(--gold)' : undefined,
                }}
                onClick={() => setDetailPlayerId(p.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <PlayerAvatar id={p.id} name={p.name} size={36} src={playerPortraitSrc(p)} />
                  <div className="row-tile-main">
                    <div className="row-tile-title">
                      {p.name}
                      <StageBadge age={p.age} />
                      {betterThanBest && players.length > 0 && (
                        <span className="pill pill-success">Mejora tu plantilla</span>
                      )}
                    </div>
                    <div className="row-tile-sub">
                      {p.age} a&ntilde;os &middot; Nivel {p.level}
                      {p.potential > p.level && ` · potencial ${formatPotential(p, scouting)}`}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (canSign) signPlayer(p)
                    }}
                    disabled={!canSign}
                    className="btn btn-success"
                    style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                  >
                    <UserPlus size={14} /> Fichar
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '0.8rem', fontSize: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>
                    Fichaje:{' '}
                    <span className={money >= p.signingFee ? 'money-positive' : 'money-negative'} style={{ fontWeight: 600 }}>
                      {p.signingFee.toLocaleString('es-ES')}€
                    </span>
                  </span>
                  <span className="money-negative">
                    {p.salary.toLocaleString('es-ES')}€/sem
                  </span>
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '0.4rem' }}>
                    <button
                      className="pill"
                      title={`Probabilidad de éxito ~${Math.round(successChance * 100)}% (mejora con un recepcionista vendedor)`}
                      style={{ cursor: p.negotiated ? 'default' : 'pointer', opacity: p.negotiated ? 0.45 : 1, border: '1px solid var(--glass-border)' }}
                      disabled={p.negotiated}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!p.negotiated) handleNegotiate(p)
                      }}
                    >
                      <Handshake size={10} /> {p.negotiated ? 'Negociado' : 'Negociar'}
                    </button>
                    <button
                      className="pill"
                      style={{ cursor: 'pointer', border: '1px solid var(--glass-border)', background: comparing ? 'var(--gold-fill)' : undefined, color: comparing ? 'var(--gold)' : undefined }}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleCompare(p.id)
                      }}
                    >
                      <Scale size={10} /> Comparar
                    </button>
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </SectionDeck>
    </div>
  )
}
